<script setup>

    import { computed, useTemplateRef, useSlots } from 'vue'
    import { useElementVisibility } from '@vueuse/core'
    import { handleBlur } from '@/Helpers'

    import { IconBookContacts28Regular, IconCalendar28Regular, IconClipboardBulletList20Regular, IconChatSparkle24Regular, IconDocumentBulletList20Regular, IconDesktopPulse28Regular, IconNotepad24Regular, IconContactCard28Regular, IconBug24Regular } from '@iconify-prerendered/vue-fluent'
    import { ExclamationTriangleIcon, TrophyIcon } from '@heroicons/vue/16/solid'
    import { BugAntIcon } from '@heroicons/vue/20/solid'

    const slots = useSlots()

    const props = defineProps({
        icon: {
            type: String, // Possible values: 'clipboard', 'calendar', 'contacts'
        },
        size: {
            type: String,
            default: 'base'
        },
        theme: {
            type: String,
            default: 'blue' // Possible values: 'blue', 'gray', 'green', 'red', 'yellow', 'purple'
        },
        tooltip: {
            type: Boolean,
            default: true
        },
        hoverable: {
            type: Boolean,
            default: true
        },
        badge: {
            type: [ String, Boolean ],
            default: false // Possible values: 'warning', 'trophy'
        },
        badgeStroke: {
            type: String,
            default: 'stroke-white'
        },
    })

    const iconRef = useTemplateRef('iconRef')

    const iconRefVisible = useElementVisibility(iconRef)

    const enableTooltips = computed(() => {
        return !!props.tooltip && iconRefVisible.value && !!slots.tooltip
    })

    const themeOptions = {
        blue: {
            base: 'text-lifeworx-blue-600',
            hover: props.hoverable ? 'hover:text-lifeworx-blue-700' : '',
        },
        gray: {
            base: 'text-zinc-400',
            hover: props.hoverable ? 'hover:text-zinc-700' : '',
        },
        lightgray: {
            base: 'text-zinc-300',
            hover: props.hoverable ? 'hover:text-zinc-500' : '',
        },
        disabled: {
            base: 'text-zinc-300 cursor-default',
            hover: props.hoverable ? 'hover:text-zinc-400' : 'hover:text-zinc-300',
        },
        green: {
            base: 'text-lifeworx-green-500/50',
            hover: props.hoverable ? 'hover:text-lifeworx-green-700/50' : '',
        },
        red: {
            base: 'text-lifeworx-red-600/50',
            hover: props.hoverable ? 'hover:text-lifeworx-red-700/75' : '',
        },
        orange: {
            base: 'text-lifeworx-orange-600/50',
            hover: props.hoverable ? 'hover:text-lifeworx-orange-700/75' : '',
        },
        yellow: {
            base: 'text-lifeworx-yellow-600/50',
            hover: props.hoverable ? 'hover:text-lifeworx-yellow-700/75' : '',
        },
        purple: {
            base: 'text-lifeworx-purple-500',
            hover: props.hoverable ? 'hover:text-lifeworx-purple-700' : '',
        },
    }

    const classes = computed(() => {
        let classList = ''
        classList += themeOptions[props.theme]?.base + ' ' + themeOptions[props.theme]?.hover

        if (props.size === 'sm') {
            classList += ' size-5'
        } else if (props.size === 'base') {
            classList += ' size-7'
        } else if (props.size === 'lg') {
            classList += ' size-9'
        }

        return classList;
    })


</script>

<template>

    <div
        ref="iconRef"
    >

        <VTooltip
            :disabled="!enableTooltips"
            @apply-hide="handleBlur"
        >
            <span class="relative inline-block">
                <div class="flex-none flex-shrink-0 inline-flex size-7">

                    <template v-if="icon === 'calendar'">
                        <IconCalendar28Regular :class="classes" />
                    </template>
                    <template v-else-if="icon === 'chat-sparkle'">
                        <IconChatSparkle24Regular :class="classes" />
                    </template>
                    <template v-else-if="icon === 'contacts'">
                        <IconBookContacts28Regular :class="classes" />
                    </template>
                    <template v-else-if="icon === 'clipboard'">
                        <IconClipboardBulletList20Regular :class="classes" />
                    </template>
                    <template v-else-if="icon === 'form'">
                        <IconDocumentBulletList20Regular :class="classes" />
                    </template>
                    <template v-else-if="icon === 'desktop-pulse'">
                        <IconDesktopPulse28Regular :class="classes" />
                    </template>
                    <template v-else-if="icon === 'notes'">
                        <IconNotepad24Regular :class="classes" />
                    </template>
                    <template v-else-if="icon === 'profile'">
                        <IconContactCard28Regular :class="classes" />
                    </template>
                    <component v-else :is="icon" :class="classes" />
                </div>
                <template v-if="badge === 'trophy' || badge === true">
                    <TrophyIcon
                        :class="[
                            'absolute bottom-0 -right-1 block size-4 rounded-full text-white stroke-[4]',
                            props.badgeStroke
                        ]"
                    />
                    <TrophyIcon
                        class="absolute bottom-0 -right-1 block size-4 text-lifeworx-yellow-500/100"
                    />
                </template>
                <template v-if="badge === 'warning' || badge === true">
                    <ExclamationTriangleIcon
                        :class="[
                        'absolute bottom-0 -right-1 block size-4 rounded-full text-white stroke-[4]',
                        props.badgeStroke
                        ]"
                    />
                    <ExclamationTriangleIcon
                        class="absolute bottom-0 -right-1 block size-4 text-lifeworx-red-500/75"
                    />
                </template>
            </span>
            <template #popper>
                <slot name="tooltip" />
            </template>
        </VTooltip>

    </div>

</template>
